import { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Menu, X, Globe, ChevronDown, User, LogOut, Package, LayoutDashboard, FileText, Settings, Shield } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';

export default function Header() {
  const { lang, setLang, page, setPage, cartCount, currentUser, logout } = useApp();
  const T = t[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [resellerMenuOpen, setResellerMenuOpen] = useState(false);
  const [scrolled] = useState(false);
  const resellerMenuRef = useRef<HTMLDivElement>(null);

  const isReseller = currentUser?.role === 'reseller';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const navItems = [
    { key: 'catalogue', label: T.nav.products, action: () => setPage('catalogue') },
    { key: 'reseller', label: T.nav.resellers, action: () => setPage('reseller') },
    { key: 'services', label: T.nav.services, action: () => setPage('services') },
    { key: 'about', label: T.nav.about, action: () => setPage('about') },
  ];

  const resellerNavItems = [
    { key: 'reseller-dashboard', label: lang === 'fr' ? 'Dashboard' : 'Dashboard', icon: <LayoutDashboard size={16} /> },
    { key: 'reseller-catalogue', label: lang === 'fr' ? 'Catalogue' : 'Catalogue', icon: <Package size={16} /> },
    { key: 'reseller-orders', label: lang === 'fr' ? 'Commandes' : 'Orders', icon: <Package size={16} /> },
    { key: 'reseller-quotes', label: lang === 'fr' ? 'Devis' : 'Quotes', icon: <FileText size={16} /> },
    { key: 'reseller-account', label: lang === 'fr' ? 'Mon compte' : 'My account', icon: <Settings size={16} /> },
  ];

  const isActive = (key: string) => page === key;

  const handleLogout = async () => {
    if (window.confirm(lang === 'fr' 
      ? 'Voulez-vous vraiment vous déconnecter ?' 
      : 'Are you sure you want to log out?')) {
      await logout();
      setUserMenuOpen(false);
      setResellerMenuOpen(false);
      setPage('home');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resellerMenuRef.current && !resellerMenuRef.current.contains(event.target as Node)) {
        setResellerMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="bg-lodiss-red text-white text-xs py-2 px-4 text-center font-medium tracking-wide">
        {T.topbar}
      </div>
      <header className={`sticky top-0 z-50 bg-white border-b border-gray-100 transition-shadow duration-200 ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => setPage('home')}
              className="flex items-center gap-2 flex-shrink-0 focus:outline-none"
            >
              <img
                src="/logo-1762256791_-_Modifie.png"
                alt="LODISS"
                className="h-10 w-auto object-contain"
              />
            </button>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(item => (
                <button
                  key={item.key}
                  onClick={item.action}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive(item.key)
                      ? 'text-lodiss-red bg-red-50'
                      : 'text-gray-700 hover:text-lodiss-red hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-colors duration-150"
              >
                <Globe size={14} />
                <span className="font-medium">{lang === 'fr' ? 'FR' : 'EN'}</span>
                <ChevronDown size={12} />
              </button>

              {isSuperAdmin && (
                <button
                  onClick={() => setPage('admin-dashboard')}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Shield size={15} />
                  {lang === 'fr' ? 'Administration' : 'Administration'}
                </button>
              )}

              {isReseller && (
                <div className="relative" ref={resellerMenuRef}>
                  <button
                    onClick={() => setResellerMenuOpen(!resellerMenuOpen)}
                    className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-lodiss-red border border-lodiss-red/30 rounded-md hover:bg-lodiss-red hover:text-white transition-colors"
                  >
                    <span className="font-medium">{lang === 'fr' ? 'Espace Revendeur' : 'Reseller Portal'}</span>
                    <ChevronDown size={14} className={`transition-transform ${resellerMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {resellerMenuOpen && (
                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          {lang === 'fr' ? 'Espace Revendeur' : 'Reseller Portal'}
                        </p>
                      </div>
                      {resellerNavItems.map(item => (
                        <button
                          key={item.key}
                          onClick={() => { setPage(item.key as any); setResellerMenuOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                            isActive(item.key)
                              ? 'text-lodiss-red bg-red-50'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      ))}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <LogOut size={16} />
                          {lang === 'fr' ? 'Déconnexion' : 'Logout'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentUser && !isReseller && !isSuperAdmin && (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    <User size={16} />
                    <span className="font-medium">{currentUser.full_name}</span>
                    <ChevronDown size={14} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                      <button
                        onClick={() => { setPage('orders'); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Package size={16} />
                        {T.common.myOrders}
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <LogOut size={16} />
                        {lang === 'fr' ? 'Déconnexion' : 'Logout'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!currentUser && (
                <>
                  <button
                    onClick={() => setPage('login')}
                    className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    <User size={16} />
                    <span className="font-medium">{lang === 'fr' ? 'Connexion' : 'Login'}</span>
                  </button>
                  <button
                    onClick={() => setPage('catalogue')}
                    className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-lodiss-red hover:bg-lodiss-dark rounded-md transition-colors duration-150"
                  >
                    {T.nav.catalogue}
                  </button>
                </>
              )}

              <button
                onClick={() => setPage('cart')}
                className="relative flex items-center justify-center w-10 h-10 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-lodiss-red transition-colors duration-150"
              >
                <ShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-lodiss-red text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md border border-gray-200 text-gray-700"
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => { item.action(); setMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive(item.key)
                      ? 'text-lodiss-red bg-red-50'
                      : 'text-gray-700 hover:text-lodiss-red hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              {isSuperAdmin && (
                <button
                  onClick={() => { setPage('admin-dashboard'); setMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive('admin-dashboard')
                      ? 'text-lodiss-red bg-red-50'
                      : 'text-gray-700 hover:text-lodiss-red hover:bg-gray-50'
                  }`}
                >
                  {lang === 'fr' ? 'Administration' : 'Administration'}
                </button>
              )}

              {isReseller && (
                <>
                  <div className="pt-2 pb-1 border-t border-gray-100 mt-2">
                    <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase">
                      {lang === 'fr' ? 'Espace Revendeur' : 'Reseller Portal'}
                    </p>
                  </div>
                  {resellerNavItems.map(item => (
                    <button
                      key={item.key}
                      onClick={() => { setPage(item.key as any); setMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                        isActive(item.key)
                          ? 'text-lodiss-red bg-red-50'
                          : 'text-gray-700 hover:text-lodiss-red hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </>
              )}

              <div className="pt-2 pb-1 flex items-center gap-2 border-t border-gray-100 mt-2">
                <button
                  onClick={() => { setLang(lang === 'fr' ? 'en' : 'fr'); setMenuOpen(false); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md"
                >
                  <Globe size={14} />
                  <span>{lang === 'fr' ? 'EN' : 'FR'}</span>
                </button>
                {currentUser ? (
                  <button
                    onClick={() => { setPage('orders'); setMenuOpen(false); }}
                    className="text-sm text-gray-600 px-3 py-1.5"
                  >
                    {T.common.myOrders}
                  </button>
                ) : (
                  <button
                    onClick={() => { setPage('login'); setMenuOpen(false); }}
                    className="text-sm text-gray-600 px-3 py-1.5"
                  >
                    {lang === 'fr' ? 'Connexion' : 'Login'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
